/**
 * "What is this thing?" (ASSET-4).
 *
 * The question a tech asks standing in front of hardware nobody told them
 * about — an unlabelled camera above a door, a panel in a cupboard, a reader
 * that has been failing since before they joined. A job-centric system cannot
 * answer it at all without being told which job installed it, which is exactly
 * what the person asking does not know.
 *
 * The backend route for this has existed since ASSET-3 and had no screen. It
 * has one now because the camera made it worth having: scanning a label is a
 * second, and typing a twenty-character serial off a dusty sticker to satisfy
 * idle curiosity is not something anyone does twice.
 *
 * It answers from the device first and the server second, in that order. The
 * device holds the assets at the sites this tech is visiting, which is where
 * they are standing, and it holds them in a comms room. The server is the
 * fallback for hardware at a site that is not on today's run.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { day, titleCase } from "../lib/format";
import * as repo from "../lib/repo";
import { serialFrom } from "../lib/scanner";
import type { Row } from "../lib/store";
import { Card, Chip, type Tone } from "../components/ui";
import { ScanButton, SerialScanner, canScan } from "./SerialScanner";

type Found = {
  asset: Row;
  siteName: string | null;
  siteId: string | null;
  /** True when this came off the device rather than the server. */
  local: boolean;
  history: Row[];
};

const TONES: Record<string, Tone> = {
  healthy: "ok",
  degraded: "warn",
  fault: "crit",
  offline: "crit",
  retired: "neutral",
};

export function Identify() {
  const [serial, setSerial] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<Found | null>(null);
  const [missing, setMissing] = useState<string | null>(null);

  async function look(raw: string) {
    const wanted = serialFrom(raw);
    if (!wanted || busy) return;
    setBusy(true);
    setFound(null);
    setMissing(null);
    try {
      const here = await repo.bySerial(wanted);
      if (here) {
        setFound({
          asset: here.asset,
          siteName: typeof here.site?.name === "string" ? here.site.name : null,
          siteId: typeof here.site?.id === "string" ? here.site.id : null,
          local: true,
          history: await repo.assetHistory(String(here.asset.id)),
        });
        return;
      }
      // Not on this phone: it belongs to a site that is not on today's run.
      // Worth asking the server, which knows the whole business.
      const asset = await api.assets.bySerial(wanted);
      setFound({
        asset: asset as unknown as Row,
        siteName: null,
        siteId: asset.site_id,
        local: false,
        history: [],
      });
    } catch (error) {
      setMissing(
        error instanceof ApiError && error.status === 0
          ? `Nothing on this phone matches ${wanted}, and there's no signal to ask.`
          : `Nothing matches ${wanted}. Check the label, or it has never been registered.`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="What is this?" subtitle="Scan a label on something already on a wall">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void look(serial);
        }}
      >
        <label className="field">
          <span>Serial</span>
          <div className={canScan() ? "field-with-action" : undefined}>
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="Scan it, or type it"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="search"
              className="mono"
            />
            <ScanButton onClick={() => setScanning(true)} />
          </div>
        </label>
        <div className="btn-row">
          <button className="btn" type="submit" disabled={busy || !serial.trim()}>
            {busy ? "Looking…" : "Look it up"}
          </button>
        </div>
      </form>

      {scanning && (
        <SerialScanner
          what="label"
          onClose={() => setScanning(false)}
          onSerial={(scanned) => {
            setScanning(false);
            setSerial(scanned);
            // Looked up straight away: unlike registering a box, there is
            // nothing to check before asking, and the tech is holding a phone
            // against a ceiling.
            void look(scanned);
          }}
        />
      )}

      {missing && (
        <p className="notice notice-warn" role="status">
          {missing}
        </p>
      )}

      {found && <Answer found={found} />}
    </Card>
  );
}

function Answer({ found }: { found: Found }) {
  const { asset } = found;
  const status = String(asset.status ?? "");
  return (
    <div className="stack" style={{ marginTop: 12 }}>
      <div className="register-head">
        <b className="mono">{String(asset.ref)}</b>
        <span className="tiny muted">{[asset.manufacturer, asset.model].filter(Boolean).map(String).join(" ")}</span>
        {status && <Chip tone={TONES[status] ?? "neutral"}>{titleCase(status)}</Chip>}
      </div>

      <dl className="kv">
        {found.siteName && (
          <>
            <dt>Where</dt>
            <dd>
              {found.siteId ? <Link to={`/sites/${found.siteId}`}>{found.siteName}</Link> : found.siteName}
              {asset.location_desc ? ` — ${String(asset.location_desc)}` : ""}
            </dd>
          </>
        )}
        {!found.siteName && Boolean(asset.location_desc) && (
          <>
            <dt>Where</dt>
            <dd>{String(asset.location_desc)}</dd>
          </>
        )}
        {Boolean(asset.installed_on) && (
          <>
            <dt>Installed</dt>
            <dd>{day(String(asset.installed_on))}</dd>
          </>
        )}
        {Boolean(asset.warranty_expires_on) && (
          <>
            <dt>Warranty</dt>
            <dd>
              {day(String(asset.warranty_expires_on))}
              {String(asset.warranty_expires_on) < new Date().toISOString().slice(0, 10) ? (
                <Chip tone="warn">expired</Chip>
              ) : null}
            </dd>
          </>
        )}
        {Boolean(asset.next_test_due_on) && (
          <>
            <dt>Next test</dt>
            <dd>{day(String(asset.next_test_due_on))}</dd>
          </>
        )}
      </dl>

      {found.history.length > 0 && (
        <>
          <p className="label">What the last techs found</p>
          <ul className="stack tiny">
            {found.history.slice(0, 4).map((event) => (
              <li key={String(event.id)}>
                <span className="muted">{day(String(event.occurred_at).slice(0, 10))}</span>{" "}
                {String(event.summary ?? titleCase(String(event.kind ?? "")))}
              </li>
            ))}
          </ul>
        </>
      )}

      {!found.local && (
        <p className="tiny muted">
          From the server — this isn't at a site on your run, so there's no history on this phone.
        </p>
      )}
    </div>
  );
}
